import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Recipe } from "../types/recipes";
import { IconChevronLeft, IconChevronRight, IconToolsKitchen3, IconWorld } from '@tabler/icons-react';

interface RecipeHeroCarouselProps {
  recipes: Recipe[];
  onRecipeClick: (recipe: Recipe) => void;
}

export const RecipeHeroCarousel = ({ recipes, onRecipeClick }: RecipeHeroCarouselProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Separate Embla instances for mobile and desktop
  const [mobileRef, mobileApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      skipSnaps: false,
      slidesToScroll: 1,
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const [desktopRef, desktopApi] = useEmblaCarousel(
    {
      loop: true,
      align: "center",
      skipSnaps: false,
      slidesToScroll: 1,
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const scrollPrev = useCallback(() => {
    mobileApi?.scrollPrev();
    desktopApi?.scrollPrev();
  }, [mobileApi, desktopApi]);

  const scrollNext = useCallback(() => {
    mobileApi?.scrollNext();
    desktopApi?.scrollNext();
  }, [mobileApi, desktopApi]);

  const scrollTo = useCallback(
    (index: number) => {
      mobileApi?.scrollTo(index);
      desktopApi?.scrollTo(index);
    },
    [mobileApi, desktopApi]
  );

  const onMobileSelect = useCallback(() => {
    if (!mobileApi) return;
    setSelectedIndex(mobileApi.selectedScrollSnap());
  }, [mobileApi]);

  const onDesktopSelect = useCallback(() => {
    if (!desktopApi) return;
    setSelectedIndex(desktopApi.selectedScrollSnap());
  }, [desktopApi]);

  useEffect(() => {
    if (!mobileApi) return;
    onMobileSelect();
    mobileApi.on("select", onMobileSelect);
    mobileApi.on("reInit", onMobileSelect);
    return () => {
      mobileApi.off("select", onMobileSelect);
      mobileApi.off("reInit", onMobileSelect);
    };
  }, [mobileApi, onMobileSelect]);

  useEffect(() => {
    if (!desktopApi) return;
    onDesktopSelect();
    desktopApi.on("select", onDesktopSelect);
    desktopApi.on("reInit", onDesktopSelect);
    return () => {
      desktopApi.off("select", onDesktopSelect);
      desktopApi.off("reInit", onDesktopSelect);
    };
  }, [desktopApi, onDesktopSelect]);

  if (recipes.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full group">
      {/* Mobile/Portrait Tablet (< 1024px): Single full-width slides */}
      <div className="block lg:hidden">
        <div className="overflow-hidden" ref={mobileRef}>
          <div className="flex">
            {recipes.map((recipe, index) => (
              <div
                key={recipe.id ?? `${recipe.RecipeTitle}-${index}`}
                className="flex-none w-full min-w-0"
              >
                <button
                  className="relative w-full text-left"
                  onClick={() => onRecipeClick(recipe)}
                >
                  {/* Badges and Title overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-4 sm:p-6 pt-12 sm:pt-16">
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2 sm:mb-4">
                      {recipe.recipe_cuisine && (
                        <Badge variant="secondary" className="text-xs sm:text-sm bg-emerald-500 text-white shadow-lg">
                          <IconWorld className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                          {recipe.recipe_cuisine.RecipeCuisine}
                        </Badge>
                      )}
                      {recipe.recipe_type && (
                        <Badge variant="default" className="text-xs sm:text-sm bg-indigo-500 text-white shadow-lg">
                          <IconToolsKitchen3 className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                          {recipe.recipe_type.RecipeType}
                        </Badge>
                      )}
                    </div>
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-8 sm:mb-12 max-w-[20rem] sm:max-w-[28rem]">
                      {recipe.RecipeTitle}
                    </h1>
                  </div>

                  {recipe.RecipeImage?.url ? (
                    <img
                      src={recipe.RecipeImage.url}
                      alt={recipe.RecipeImage.alternativeText || recipe.RecipeTitle}
                      className="w-full aspect-[2/1] object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-[2/1] bg-muted flex items-center justify-center">
                      <span className="text-muted-foreground">No image</span>
                    </div>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Navigation Arrows */}
        {recipes.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black hover:text-black z-20 h-10 w-10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={scrollPrev}
              aria-label="Previous slide"
            >
              <IconChevronLeft className="h-6 w-6 text-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black hover:text-black z-20 h-10 w-10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={scrollNext}
              aria-label="Next slide"
            >
              <IconChevronRight className="h-6 w-6 text-foreground" />
            </Button>
          </>
        )}
      </div>

      {/* Desktop (>= 1024px): Three-panel peek design */}
      <div className="hidden lg:block">
        <div className="overflow-hidden" ref={desktopRef}>
          <div className="flex">
            {recipes.map((recipe, index) => (
              <div
                key={recipe.id ?? `${recipe.RecipeTitle}-${index}`}
                className={cn(
                  "flex-none min-w-0 transition-opacity duration-300",
                  "w-[64%] mr-[15px]",
                  index === selectedIndex ? "opacity-100" : "opacity-70"
                )}
              >
                <button
                  className="relative w-full text-left"
                  onClick={() => onRecipeClick(recipe)}
                >
                  {/* Badges and Title overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-8 pt-20">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {recipe.recipe_cuisine && (
                        <Badge variant="secondary" className="text-sm bg-emerald-500 text-white shadow-lg">
                          <IconWorld className="h-3 w-3 mr-1" />
                          {recipe.recipe_cuisine.RecipeCuisine}
                        </Badge>
                      )}
                      {recipe.recipe_type && (
                        <Badge variant="default" className="text-sm bg-indigo-500 text-white shadow-lg">
                          <IconToolsKitchen3 className="h-3 w-3 mr-1" />
                          {recipe.recipe_type.RecipeType}
                        </Badge>
                      )}
                    </div>
                    <h2 className="text-5xl lg:text-6xl font-bold text-white mb-12 max-w-[35rem]">
                      {recipe.RecipeTitle}
                    </h2>
                  </div>

                  {recipe.RecipeImage?.url ? (
                    <img
                      src={recipe.RecipeImage.url}
                      alt={recipe.RecipeImage.alternativeText || recipe.RecipeTitle}
                      className="w-full aspect-[2/1] object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-[2/1] bg-muted flex items-center justify-center">
                      <span className="text-muted-foreground">No image</span>
                    </div>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop Navigation Arrows */}
        {recipes.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black hover:text-black z-30 h-12 w-12 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={scrollPrev}
              aria-label="Previous slide"
            >
              <IconChevronLeft className="h-8 w-8 text-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black hover:text-black z-30 h-12 w-12 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={scrollNext}
              aria-label="Next slide"
            >
              <IconChevronRight className="h-8 w-8 text-foreground" />
            </Button>
          </>
        )}
      </div>

      {/* Dots indicator */}
      {recipes.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-30">
          {recipes.map((_, index) => (
            <button
              key={index}
              className={cn(
                "h-4 rounded-full transition-all duration-300",
                index === selectedIndex
                  ? "w-8 bg-[#fafafa]"
                  : "w-4 bg-white/60 hover:bg-white/80"
              )}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};